import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuthContext } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import BottomNav from "@/components/BottomNav";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Entry from "./pages/Entry";
import Onboarding from "./pages/Onboarding";
import Matches from "./pages/Matches";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Portal from "./pages/Portal";
import LiveArenas from "./pages/LiveArenas";
import LoveExhibition from "./pages/LoveExhibition";
import ArenaRecordingsPlayback from "./pages/ArenaRecordingsPlayback";
import Notifications from "./pages/Notifications";
import VibeFeed from "./pages/VibeFeed";
import Admin from "./pages/Admin";
import GlobalConnect from "./pages/GlobalConnect";
import GlobalConnectDiscovery from "./pages/GlobalConnectDiscovery";
import GlobalConnectScene from "./pages/GlobalConnectScene";
import Religion from "./pages/Religion";
import PopBalloon from "./pages/PopBalloon";
import NotFound from "./pages/NotFound";
import VoiceDiscovery from "./pages/VoiceDiscovery";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Check if user needs onboarding (no photo_url means incomplete profile)
const OnboardingGate = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, profile, loading } = useAuthContext();

  if (loading) {
    return (
      <div className="min-h-screen sky-gradient flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/entry" replace />;
  }

  // If user has no photo, redirect to onboarding
  if (profile && !profile.photo_url) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuthContext();

  if (loading) {
    return (
      <div className="min-h-screen sky-gradient flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/entry" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const { isAuthenticated, profile, loading } = useAuthContext();
  const location = useLocation();

  // Hide bottom nav on onboarding and profile screens
  const hideBottomNav = location.pathname === "/onboarding" || location.pathname === "/profile";

  // Determine if bottom nav should show (only for completed profiles, and not on onboarding/profile screens)
  const showBottomNav = isAuthenticated && profile?.photo_url && !hideBottomNav;

  // Handle OAuth callback - Supabase processes hash fragments automatically
  // but we can add logging to debug
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash;
      if (hash && hash.includes('access_token')) {
        console.log("OAuth callback detected in URL hash");
      }
    }
  }, []);

  return (
    <>
      <Routes>
        {/* Entry/Landing page - shows for unauthenticated users */}
        <Route path="/" element={isAuthenticated ? <Navigate to="/home" replace /> : <Entry />} />
        <Route path="/entry" element={isAuthenticated ? <Navigate to="/home" replace /> : <Entry />} />
        {/* Auth page - accessed after selecting phone/email from entry screen */}
        <Route path="/auth" element={isAuthenticated ? <Navigate to="/home" replace /> : <Auth />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/home" element={<OnboardingGate><Index /></OnboardingGate>} />
        <Route path="/pop-balloon" element={<OnboardingGate><PopBalloon /></OnboardingGate>} />
        <Route path="/matches" element={<OnboardingGate><Matches /></OnboardingGate>} />
        <Route path="/profile" element={<OnboardingGate><Profile /></OnboardingGate>} />
        <Route path="/settings" element={<OnboardingGate><Settings /></OnboardingGate>} />
        <Route path="/portal" element={<OnboardingGate><Portal /></OnboardingGate>} />
        <Route path="/live-arenas" element={<OnboardingGate><LiveArenas /></OnboardingGate>} />
        <Route path="/love-exhibition" element={<OnboardingGate><LoveExhibition /></OnboardingGate>} />
        <Route path="/arena-recordings/:recordingId" element={<OnboardingGate><ArenaRecordingsPlayback /></OnboardingGate>} />
        <Route path="/notifications" element={<OnboardingGate><Notifications /></OnboardingGate>} />
        <Route path="/vibe-feed" element={<OnboardingGate><VibeFeed /></OnboardingGate>} />
        <Route path="/admin" element={<OnboardingGate><Admin /></OnboardingGate>} />
        <Route path="/global-connect" element={<OnboardingGate><GlobalConnect /></OnboardingGate>} />
        <Route path="/global-connect/:countryCode" element={<OnboardingGate><GlobalConnectDiscovery /></OnboardingGate>} />
        <Route path="/global-connect/:countryCode/scene/:cityId/:scene" element={<OnboardingGate><GlobalConnectScene /></OnboardingGate>} />
        <Route path="/religion" element={<OnboardingGate><Religion /></OnboardingGate>} />
        <Route path="/voice-discovery" element={<OnboardingGate><VoiceDiscovery /></OnboardingGate>} />
        <Route path="/teleport" element={<Navigate to="/portal" replace />} />
        <Route path="/teleport/*" element={<Navigate to="/portal" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {showBottomNav && <BottomNav />}
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
